import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import { MarqueeBadge, MarqueeConfig } from '../types';
import { perfTracker } from './perfLogger';

export const DEFAULT_MARQUEE_BADGES: MarqueeBadge[] = [
  { id: 'mb-1', title: 'Passport', iconName: 'FileText', glowColor: '#38bdf8', isVisible: true, sortOrder: 1 },
  { id: 'mb-2', title: 'ID Card', iconName: 'CreditCard', glowColor: '#22c55e', isVisible: true, sortOrder: 2 },
  { id: 'mb-3', title: 'Driving License', iconName: 'ShieldCheck', glowColor: '#a855f7', isVisible: true, sortOrder: 3 },
  { id: 'mb-4', title: 'Certificate', iconName: 'Award', glowColor: '#f59e0b', isVisible: true, sortOrder: 4 },
  { id: 'mb-5', title: 'Utility Bill', iconName: 'Receipt', glowColor: '#38bdf8', isVisible: true, sortOrder: 5 },
  { id: 'mb-6', title: 'Bank Statement', iconName: 'Building', glowColor: '#06b6d4', isVisible: true, sortOrder: 6 },
  { id: 'mb-7', title: 'Resume', iconName: 'FileCheck', glowColor: '#ec4899', isVisible: true, sortOrder: 7 },
  { id: 'mb-8', title: 'Health ID', iconName: 'Briefcase', glowColor: '#3b82f6', isVisible: true, sortOrder: 8 },
  { id: 'mb-9', title: 'Residence Permit', iconName: 'Layers', glowColor: '#10b981', isVisible: true, sortOrder: 9 },
  { id: 'mb-10', title: 'Visa Documents', iconName: 'Globe', glowColor: '#8b5cf6', isVisible: true, sortOrder: 10 },
  { id: 'mb-11', title: 'Editable PSD', iconName: 'FileCode', glowColor: '#0284c7', isVisible: true, sortOrder: 11 },
  { id: 'mb-12', title: 'AI Templates', iconName: 'Sparkles', glowColor: '#f43f5e', isVisible: true, sortOrder: 12 },
  { id: 'mb-13', title: 'CDR Files', iconName: 'Palette', glowColor: '#eab308', isVisible: true, sortOrder: 13 },
  { id: 'mb-14', title: 'Vector Files', iconName: 'Shapes', glowColor: '#14b8a6', isVisible: true, sortOrder: 14 },
  { id: 'mb-15', title: 'Premium Resources', iconName: 'Star', glowColor: '#f97316', isVisible: true, sortOrder: 15 },
];

export const DEFAULT_MARQUEE_CONFIG: MarqueeConfig = {
  enabled: true,
  speedDurationSeconds: 30,
  pauseOnHover: true,
  sectionTitle: 'Explore Marketplace Collections',
  sectionSubtitle: 'Instant access to high-precision 3D vector, PSD, and AI document templates',
  badges: DEFAULT_MARQUEE_BADGES,
};

const COLLECTION_NAME = 'marquee_settings';
const DOC_ID = 'config';
const CACHE_KEY = 'rh_marquee_config';
const PATH_KEY = `${COLLECTION_NAME}/${DOC_ID}`;

let cachedMarqueeConfig: MarqueeConfig = { ...DEFAULT_MARQUEE_CONFIG };

try {
  const saved = localStorage.getItem(CACHE_KEY);
  if (saved) {
    cachedMarqueeConfig = { ...DEFAULT_MARQUEE_CONFIG, ...JSON.parse(saved) };
  }
} catch (e) {}

export function getLocalMarqueeConfig(): MarqueeConfig {
  return cachedMarqueeConfig;
}

const marqueeSubscribers = new Set<(config: MarqueeConfig) => void>();
let activeMarqueeUnsub: Unsubscribe | null = null;

export async function fetchMarqueeConfigFromFirestore(): Promise<MarqueeConfig> {
  const startTime = performance.now();
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const snap = await getDoc(docRef);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(PATH_KEY, duration, snap.metadata.fromCache ? 'cache' : 'server', 1);

    if (snap.exists()) {
      const data = snap.data();
      const merged: MarqueeConfig = {
        ...DEFAULT_MARQUEE_CONFIG,
        ...data,
        badges: Array.isArray(data.badges) && data.badges.length > 0 ? data.badges : DEFAULT_MARQUEE_BADGES,
      };
      cachedMarqueeConfig = merged;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
      } catch (e) {}
      return merged;
    }
  } catch (err) {
    console.warn('Error fetching Marquee Config from Firestore:', err);
  }
  return cachedMarqueeConfig;
}

export function subscribeToMarqueeConfigFromFirestore(
  callback: (config: MarqueeConfig) => void
): () => void {
  callback(cachedMarqueeConfig);

  marqueeSubscribers.add(callback);

  fetchMarqueeConfigFromFirestore().then((cfg) => {
    if (cfg) {
      cachedMarqueeConfig = cfg;
      marqueeSubscribers.forEach((cb) => {
        try { cb(cfg); } catch (e) {}
      });
    }
  }).catch((err) => {
    console.warn('One-time marquee config fetch error:', err);
  });

  return () => {
    marqueeSubscribers.delete(callback);
  };
}

export async function saveMarqueeConfigToFirestore(config: MarqueeConfig): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  const cleanConfig: MarqueeConfig = {
    enabled: config.enabled ?? true,
    speedDurationSeconds: Math.max(5, Math.min(120, config.speedDurationSeconds || 30)),
    pauseOnHover: config.pauseOnHover ?? true,
    sectionTitle: config.sectionTitle || 'Explore Marketplace Collections',
    sectionSubtitle: config.sectionSubtitle || 'Instant access to high-precision 3D vector, PSD, and AI document templates',
    badges: config.badges.map((b, idx) => ({
      id: b.id || `mb-${idx + 1}`,
      title: b.title.trim(),
      iconName: b.iconName.trim() || 'FileText',
      glowColor: b.glowColor || '#38bdf8',
      isVisible: b.isVisible ?? true,
      sortOrder: typeof b.sortOrder === 'number' ? b.sortOrder : idx + 1,
    })),
  };

  cachedMarqueeConfig = cleanConfig;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cleanConfig));
  } catch (e) {}

  try {
    await setDoc(docRef, cleanConfig, { merge: true });
  } catch (err) {
    console.warn('Failed to save Marquee Config to Firestore:', err);
  }
}
